import AsyncStorage from '@react-native-async-storage/async-storage';
import { useEffect, useRef, useState } from 'react';

interface UseAsyncStorageOptions {}

export const useAsyncStorage = <T extends unknown>(key: string, options: UseAsyncStorageOptions = {}) => {
  const [value, setValue] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    get();

    return () => {
      mounted.current = false;
    };
  }, []);

  const get = async () => {
    setLoading(true);
    try {
      const value = await AsyncStorage.getItem(key);
      if (mounted.current) {
        const parsedValue = value ? JSON.parse(value) : null;
        setValue(parsedValue);
      } else {
        setValue(null);
      }
    } catch (error) {
      if (mounted.current) {
        setError(error);
      }
    } finally {
      if (mounted.current) {
        setLoading(false);
      }
    }
  };

  const set = async (value: T) => {
    setLoading(true);
    try {
      const stringifiedValue = JSON.stringify(value);
      await AsyncStorage.setItem(key, stringifiedValue);
      if (mounted.current) {
        setValue(value);
      }
    } catch (error) {
      if (mounted.current) {
        setError(error);
      }
    } finally {
      if (mounted.current) {
        setLoading(false);
      }
    }
  };

  const remove = async () => {
    setLoading(true);
    try {
      await AsyncStorage.removeItem(key);
      if (mounted.current) {
        setValue(null);
      }
    } catch (error) {
      if (mounted.current) {
        setError(error);
      }
    } finally {
      if (mounted.current) {
        setLoading(false);
      }
    }
  };

  return { value, error, loading, get, set, remove };
};
